import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
	ActionIcon,
	Alert,
	Anchor,
	Badge,
	Box,
	Button,
	Card,
	Center,
	Drawer,
	Flex,
	Group,
	Loader,
	Modal,
	Paper,
	ScrollArea,
	Space,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { DateInput, DatePicker, TimeInput } from "@mantine/dates";
import { type FormErrors, useForm } from "@mantine/form";
import { useDisclosure, usePrevious } from "@mantine/hooks";
import {
	IconAlertTriangle,
	IconBuildingSkyscraper,
	IconCalendarFilled,
	IconClock,
	IconClockFilled,
	IconCloudDownload,
	IconDeviceMobile,
	IconFileDots,
	IconHomeFilled,
	IconMapPinFilled,
	IconMapPinOff,
	IconPhoneFilled,
	IconReportMedical,
} from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import {
	useCaregiverAppCheckInOutMutation,
	useCaregiverAppGetShiftsQuery,
} from "@/setup/frontend/api/apiHooks";
import { CaregiverAppSettingsButton } from "@/caregiver-app/components/CaregiverAppSettingsButton";
import { combineDateAndTime } from "@/common/utils/time-utils";
import { useAsyncNotifications } from "@/common/hooks/useAsyncNotifications";
import type { ActionInput, ActionOutput } from "@/setup/backend/types";

import { getCurrentPosition } from "@/common/hooks/getCurrentPosition";
import { parseAsString, useQueryState } from "nuqs";
import { getStableShiftsQueryAfterCursor } from "../utils";
import { useTRPC } from "@/common/utils/trpc";
import { ROUTES } from "@/setup/frontend/routes";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);

function useGetGpsLocation() {
	const mutation = useMutation<GeolocationPosition, GeolocationPositionError>({
		mutationFn: async () => {
			return getCurrentPosition({
				enableHighAccuracy: true,
				maximumAge: 5000, // 5 seconds
				timeout: 60000,
			}).catch((e) => {
				if (e.code === 2) {
					return getCurrentPosition({
						timeout: 60000,
						maximumAge: 5000 * 60, // 5 minutes
						enableHighAccuracy: true,
					});
				}

				throw e;
			});
		},
	});

	return mutation;
}

function makeGeolocationPositionSerializable(
	position: GeolocationPosition | null | undefined,
) {
	// implicit json serialization does not seem to work the same between ios and android
	// this function explicitly constructs the object so it can be serialized

	return position
		? {
				coords: {
					latitude: position.coords?.latitude || null,
					longitude: position.coords?.longitude || null,
					altitude: position.coords?.altitude || null,
					accuracy: position.coords?.accuracy || null,
					altitudeAccuracy: position.coords?.altitudeAccuracy || null,
					heading: position.coords?.heading || null,
					speed: position.coords?.speed || null,
				},
				timestamp: position.timestamp || null,
			}
		: null;
}

type SerializableGeolocationPosition = ReturnType<
	typeof makeGeolocationPositionSerializable
>;

export default function CaregiverAppIndexPage() {
	const [startTimestamp] = useQueryState("startTimestamp");
	const [statusFilter] = useQueryState(
		"status",
		parseAsString.withDefault("scheduled"),
	);
	const getGpsLocationMutation = useGetGpsLocation();

	const [selectedTimestamp, setSelectedTimestamp] = useState(() =>
		startTimestamp ? dayjs.utc(startTimestamp).local() : dayjs(),
	);

	const afterTimestamp = getStableShiftsQueryAfterCursor(selectedTimestamp);
	const shiftsQuery = useCaregiverAppGetShiftsQuery({
		after: afterTimestamp,
		status:
			statusFilter === "scheduled"
				? undefined
				: (statusFilter as "missing" | undefined),
	});

	const flattenedShifts = useMemo(() => {
		return shiftsQuery.data?.pages.flat() ?? [];
	}, [shiftsQuery.data]);

	const dayHeadersMap = useMemo(() => {
		const map = new Map<number, string>();
		flattenedShifts.forEach((shift, index) => {
			const prev = flattenedShifts[index - 1];
			const prevDay = prev
				? dayjs.utc(prev.startTimestamp).local().format("ddd MMM D")
				: undefined;
			const currentDay = dayjs
				.utc(shift.startTimestamp)
				.local()
				.format("ddd MMM D");
			if (prevDay !== currentDay) {
				map.set(shift.id, currentDay);
			}
		});
		return map;
	}, [flattenedShifts]);

	const dayBillableHoursMap = useMemo(() => {
		const map = new Map<string, number>();
		for (const shift of flattenedShifts) {
			const day = dayjs.utc(shift.startTimestamp).local().format("ddd MMM D");
			map.set(day, (map.get(day) || 0) + Number(shift.billableHours || 0));
		}
		return map;
	}, [flattenedShifts]);

	const closestShiftToSelectedTimestamp = useMemo(() => {
		if (!flattenedShifts.length) return null;

		let closestShift = flattenedShifts[0];
		let smallestDiff = Math.abs(
			dayjs
				.utc(closestShift.startTimestamp)
				.local()
				.diff(selectedTimestamp, "minute"),
		);

		for (const shift of flattenedShifts) {
			const diff = Math.abs(
				dayjs
					.utc(shift.startTimestamp)
					.local()
					.diff(selectedTimestamp, "minute"),
			);
			if (diff < smallestDiff) {
				smallestDiff = diff;
				closestShift = shift;
			}
		}

		return closestShift;
	}, [flattenedShifts]);

	const [isDatePickerOpen, datePickerControls] = useDisclosure(false);

	return (
		<Flex direction="column" style={{ overflow: "hidden" }} flex={1}>
			<Drawer
				opened={isDatePickerOpen}
				onClose={datePickerControls.close}
				position="bottom"
				size="100%"
			>
				<Center>
					<Stack align="start" w="360px">
						<Stack gap={0} align="start">
							<Title order={3}>Select Date</Title>
							<Text>Select a date to view shifts for that day</Text>
						</Stack>
						<DatePicker
							size="lg"
							value={selectedTimestamp.toDate()}
							highlightToday
							onChange={(date) => {
								setSelectedTimestamp(dayjs(date));
								datePickerControls.close();
							}}
						/>
					</Stack>
				</Center>
			</Drawer>

			<Box style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
				<Group h={64} px="md" justify="space-between">
					<CaregiverAppSettingsButton />

					<ActionIcon
						variant="default"
						size="36px"
						onClick={datePickerControls.open}
					>
						<IconCalendarFilled />
					</ActionIcon>
				</Group>
			</Box>

			<Stack p="sm" flex={1} style={{ overflow: "auto" }}>
				{getGpsLocationMutation.error?.code &&
				getGpsLocationMutation.error?.code ===
					getGpsLocationMutation.error?.PERMISSION_DENIED ? (
					<Stack>
						<Alert
							icon={<IconMapPinOff size={16} />}
							title="GPS Disabled"
							color="red"
							variant="outline"
						>
							<Text>
								You must enable GPS for your check in and check out to be EVV
								compliant.
							</Text>{" "}
							<Text>
								We strongly recommend using Chrome for Android and Safari for
								iOS.
							</Text>
							<Text>
								On Safari, tap the 'Aa' icon on the left side of the address
								bar. Proceed to 'Website Settings' and change Location to
								'Allow'.
							</Text>
							<Text>
								On Android Chrome, tap the settings icon to the left of the
								address bar. Proceed to 'Permissions' and toggle Location to
								'Allowed'.
							</Text>
							<Text>Once you have enabled GPS, refresh the page.</Text>
						</Alert>
					</Stack>
				) : null}
				{getGpsLocationMutation.error?.code &&
				getGpsLocationMutation.error?.code ===
					getGpsLocationMutation.error?.POSITION_UNAVAILABLE ? (
					<Stack>
						<Alert
							icon={<IconMapPinOff size={16} />}
							title="GPS Unavailable"
							color="red"
							variant="outline"
						>
							GPS is unavailable. Check in and check out will not be EVV
							compliant. Ensure you are on a device with GPS capabilities.
						</Alert>
					</Stack>
				) : null}
				{getGpsLocationMutation.error?.code &&
				getGpsLocationMutation.error?.code ===
					getGpsLocationMutation.error?.TIMEOUT ? (
					<Stack>
						<Alert
							icon={<IconMapPinOff size={16} />}
							title="GPS Timeout"
							color="red"
							variant="outline"
						>
							GPS timed out. Check in and check out will not be EVV compliant.
							First refresh the page and try again. If the issue persists, try
							closing and reopening your browser app or restarting your device.
						</Alert>
					</Stack>
				) : null}

				<ScrollArea offsetScrollbars>
					<Stack>
						{shiftsQuery.hasPreviousPage ? (
							<Button
								variant="light"
								onClick={() => shiftsQuery.fetchPreviousPage()}
								disabled={
									!shiftsQuery.hasPreviousPage ||
									shiftsQuery.isFetchingPreviousPage
								}
							>
								{shiftsQuery.isFetchingPreviousPage
									? "Loading more..."
									: shiftsQuery.hasPreviousPage
										? "Load More"
										: "Nothing more to load"}
							</Button>
						) : null}

						{flattenedShifts.map((shift, i) => {
							return (
								<ShiftCard
									key={shift.id.toString()}
									shift={shift}
									headerDateString={dayHeadersMap.get(shift.id)}
									dayBillableHoursMap={dayBillableHoursMap}
									isClosestShiftToSelectedTimestamp={
										closestShiftToSelectedTimestamp?.id === shift.id
									}
									isFirst={i === 0}
									isGpsLoading={getGpsLocationMutation.isPending}
									getGpsLocation={getGpsLocationMutation.mutateAsync}
									isSelected={Boolean(
										dayHeadersMap.get(shift.id) &&
											dayjs
												.utc(shift.startTimestamp)
												.local()
												.isSame(selectedTimestamp, "day"),
									)}
								/>
							);
						})}
						{!shiftsQuery.data && shiftsQuery.isLoading ? (
							<Center pt="236px">
								<Loader size="md" />
							</Center>
						) : null}
						{shiftsQuery.hasNextPage ? (
							<Button
								variant="light"
								onClick={() => shiftsQuery.fetchNextPage()}
								disabled={
									!shiftsQuery.hasNextPage || shiftsQuery.isFetchingNextPage
								}
							>
								{shiftsQuery.isFetchingNextPage
									? "Loading more..."
									: shiftsQuery.hasNextPage
										? "Load More"
										: "Nothing more to load"}
							</Button>
						) : null}
					</Stack>
				</ScrollArea>
			</Stack>
		</Flex>
	);
}

function ShiftCard({
	shift,
	headerDateString,
	isGpsLoading,
	getGpsLocation,
	isFirst,
	isClosestShiftToSelectedTimestamp,
	isSelected,
	dayBillableHoursMap,
}: {
	shift: ActionOutput<"caregiverAppGetShifts">[number];
	headerDateString?: string;
	getGpsLocation: () => Promise<GeolocationPosition>;
	isGpsLoading: boolean;
	isClosestShiftToSelectedTimestamp: boolean;
	isFirst?: boolean;
	isSelected?: boolean;
	dayBillableHoursMap: Map<string, number>;
}) {
	const checkInInputRef = useRef<HTMLInputElement>(null);
	const checkOutInputRef = useRef<HTMLInputElement>(null);
	const scrollIntoViewTargetRef = useRef<HTMLHeadingElement>(null);
	const checkInOutMutation = useCaregiverAppCheckInOutMutation();
	const wasFirst = usePrevious(isFirst);
	const didScrollSelectedIntoView = useRef(false);
	const [isCallPOCModalOpen, callPOCModalControls] = useDisclosure(false);
	const [isActionsDrawerOpen, actionsDrawerControls] = useDisclosure(false);

	const form = useForm<
		ActionInput<"caregiverAppCheckInOut"> & {
			checkInDate?: string;
			checkOutDate?: string;
		}
	>({
		validate: (values) => {
			const errors: FormErrors = {};
			if (
				values.checkIn &&
				values.checkOut &&
				values.checkInDate &&
				values.checkOutDate
			) {
				const resolvedCheckIn = combineDateAndTime(
					values.checkInDate,
					values.checkIn,
				);
				const resolvedCheckOut = combineDateAndTime(
					values.checkOutDate,
					values.checkOut,
				);

				if (!resolvedCheckIn.isBefore(resolvedCheckOut)) {
					errors.checkOut = "Check out time must be after check in time";
				}
			}
			return errors;
		},
	});

	useAsyncNotifications({
		isSuccess: checkInOutMutation.isSuccess,
		isError: checkInOutMutation.isError,
		error: {
			title: "Error",
			message: checkInOutMutation.error?.message,
			color: "red",
		},
	});

	useEffect(() => {
		const checkInTime = shift.checkIn
			? dayjs.utc(shift.checkIn).local().format("HH:mm")
			: undefined;
		const checkOutTime = shift.checkOut
			? dayjs.utc(shift.checkOut).local().format("HH:mm")
			: undefined;

		form.setInitialValues({
			scheduleId: shift.id,
			checkIn: checkInTime,
			checkOut: checkOutTime,
			// EVV/quick check-in/out default to today's date; manual entry defaults
			// to the scheduled start/end date since the caregiver is filling it in after the fact
			checkInDate: shift.checkIn
				? dayjs.utc(shift.checkIn).local().format("YYYY-MM-DD")
				: shift.quickCheckInEnabled
					? dayjs().format("YYYY-MM-DD")
					: dayjs.utc(shift.startTimestamp).local().format("YYYY-MM-DD"),
			checkOutDate: shift.checkOut
				? dayjs.utc(shift.checkOut).local().format("YYYY-MM-DD")
				: shift.quickCheckOutEnabled
					? dayjs().format("YYYY-MM-DD")
					: dayjs.utc(shift.endTimestamp).local().format("YYYY-MM-DD"),
		});
		form.reset();
	}, [shift.id, shift.checkIn, shift.checkOut]);

	useEffect(() => {
		if (checkInOutMutation.isSuccess) {
			form.resetDirty();
		}
	}, [checkInOutMutation.isSuccess]);

	useEffect(() => {
		if (isSelected && !didScrollSelectedIntoView.current) {
			scrollIntoViewTargetRef.current?.scrollIntoView({
				behavior: "auto",
			});
			didScrollSelectedIntoView.current = true;
		}
	}, [isSelected]);

	useEffect(() => {
		if (wasFirst && !isFirst) {
			scrollIntoViewTargetRef.current?.scrollIntoView({
				behavior: "auto",
			});
		}
	}, [wasFirst, isFirst]);

	useEffect(() => {
		if (isClosestShiftToSelectedTimestamp) {
			scrollIntoViewTargetRef.current?.scrollIntoView({
				behavior: "auto",
			});
		}
	}, [isClosestShiftToSelectedTimestamp]);

	const getGpsForCheckInOut = useCallback(async () => {
		const [gpsLocation, err] = shift.evvConsentGranted
			? await getGpsLocation()
					.then(
						(res) =>
							[makeGeolocationPositionSerializable(res), null] as [
								SerializableGeolocationPosition,
								null,
							],
					)
					.catch(
						(err) =>
							[
								null,
								{
									code: err?.code?.toString() ?? "UNKNOWN",
									message: err?.message || "Unknown error",
								},
							] as [null, { code: string; message: string }],
					)
			: [
					null,
					{
						code: "PERMISSION_DENIED",
						message: "client evv consent not granted",
					},
				];

		const isStale = gpsLocation?.timestamp
			? dayjs(gpsLocation?.timestamp).isBefore(dayjs().subtract(5, "minute"))
			: null;
		return [
			gpsLocation,
			err
				? err
				: isStale
					? { code: "STALE", message: "GPS data older than 5 minutes" }
					: null,
		] as [
			SerializableGeolocationPosition | null,
			GeolocationPositionError | Error | null,
		];
	}, [getGpsLocation]);

	const onQuickCheckin = useCallback(async () => {
		const [gpsLocation, err] = await getGpsForCheckInOut();
		checkInOutMutation.mutate({
			scheduleId: shift.id,
			checkIn: dayjs().toISOString(),
			checkInType: 2,
			checkInLatitude:
				!err && gpsLocation?.coords.latitude
					? gpsLocation?.coords.latitude
					: undefined,
			checkInLongitude:
				!err && gpsLocation?.coords.longitude
					? gpsLocation?.coords.longitude
					: undefined,
			checkInMetadata: {
				gpsError: err,
				gpsData: gpsLocation,
				userAgent: navigator.userAgent,
				appVersion: import.meta.env.VITE_APP_VERSION || "unknown",
			},
		});
	}, [shift.id, checkInOutMutation, getGpsForCheckInOut]);

	const onQuickCheckOut = useCallback(async () => {
		const [gpsLocation, err] = await getGpsForCheckInOut();
		checkInOutMutation.mutate({
			scheduleId: shift.id,
			checkOut: dayjs().toISOString(),
			checkOutType: 2,
			checkOutLatitude:
				!err && gpsLocation?.coords.latitude
					? gpsLocation?.coords.latitude
					: undefined,
			checkOutLongitude:
				!err && gpsLocation?.coords.longitude
					? gpsLocation?.coords.longitude
					: undefined,
			checkOutMetadata: {
				gpsError: err,
				gpsData: gpsLocation,
				userAgent: navigator.userAgent,
				appVersion: import.meta.env.VITE_APP_VERSION || "unknown",
			},
		});
	}, [shift.id, checkInOutMutation, getGpsForCheckInOut]);

	const onManualCheckIn = useCallback(async () => {
		form.onSubmit(async (values) => {
			const [gpsLocation, err] = await getGpsForCheckInOut();
			const proposedDate =
				values.checkIn && values.checkInDate
					? combineDateAndTime(values.checkInDate, values.checkIn)
					: null;

			checkInOutMutation.mutate({
				scheduleId: values.scheduleId,
				checkIn: proposedDate?.toISOString(),
				checkInType: values.checkIn ? 2 : undefined,
				checkInLatitude:
					!err && gpsLocation?.coords.latitude
						? gpsLocation?.coords.latitude
						: undefined,
				checkInLongitude:
					!err && gpsLocation?.coords.longitude
						? gpsLocation?.coords.longitude
						: undefined,
				checkInMetadata: {
					gpsError: err,
					gpsData: gpsLocation,
					userAgent: navigator.userAgent,
					appVersion: import.meta.env.VITE_APP_VERSION || "unknown",
				},
			});
		})();
	}, [shift.id, checkInOutMutation, form, getGpsForCheckInOut]);

	const onManualCheckOut = useCallback(() => {
		form.onSubmit(async (values) => {
			const [gpsLocation, err] = await getGpsForCheckInOut();
			const proposedDate =
				values.checkOut && values.checkOutDate
					? combineDateAndTime(values.checkOutDate, values.checkOut)
					: null;

			checkInOutMutation.mutate({
				scheduleId: values.scheduleId,
				checkOut: proposedDate?.toISOString(),
				checkOutType: 2,
				checkOutLatitude:
					!err && gpsLocation?.coords.latitude
						? gpsLocation?.coords.latitude
						: undefined,
				checkOutLongitude:
					!err && gpsLocation?.coords.longitude
						? gpsLocation?.coords.longitude
						: undefined,
				checkOutMetadata: {
					gpsError: err,
					gpsData: gpsLocation,
					userAgent: navigator.userAgent,
					appVersion: import.meta.env.VITE_APP_VERSION || "unknown",
				},
			});
		})();
	}, [shift.id, checkInOutMutation, form, getGpsForCheckInOut]);

	function formatPhoneNumber(phoneNumber: string | undefined | null): string {
		if (!phoneNumber) {
			return "N/A";
		}
		const cleaned = phoneNumber.replace(/\D/g, "");

		if (cleaned.length === 10) {
			return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
		}
		if (cleaned.length === 11 && cleaned[0] === "1") {
			return cleaned.slice(1).replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
		}
		return phoneNumber;
	}

	const getStatusBadge = (
		shift: ActionOutput<"caregiverAppGetShifts">[number],
	) => {
		if (shift.cancelled) {
			return (
				<Badge size="sm" color="gray">
					Cancelled
				</Badge>
			);
		}

		if (shift.missed) {
			return (
				<Badge size="xs" color="red">
					Missed
				</Badge>
			);
		}
		if (shift.complete) {
			return (
				<Badge size="xs" color="green">
					Complete
				</Badge>
			);
		}
		// to avoid caregiver confusion, show complete badge if check in and check out are both set
		if (shift.checkIn && shift.checkOut) {
			return (
				<Badge size="xs" color="green">
					Complete
				</Badge>
			);
		}
		if (shift.inProgress) {
			return (
				<Badge size="xs" color="yellow">
					In Progress
				</Badge>
			);
		}
		if (shift.tasksMissing) {
			return (
				<Badge size="xs" color="orange">
					Missing Tasks
				</Badge>
			);
		}

		if (shift.timeEntryMissing) {
			return (
				<Badge size="xs" color="orange">
					Missing Time Entry
				</Badge>
			);
		}
		if (shift.assigned) {
			return <Badge color="blue">Assigned</Badge>;
		}

		return (
			<Badge size="xs" color="cyan">
				Unassigned
			</Badge>
		);
	};

	const checkInDate =
		form.values.checkIn && form.values.checkInDate
			? combineDateAndTime(form.values.checkInDate, form.values.checkIn)
			: null;
	const checkInDiff = checkInDate
		? Math.abs(
				checkInDate.diff(dayjs.utc(shift.startTimestamp).local(), "minutes"),
			)
		: null;

	const checkOutDate =
		form.values.checkOut && form.values.checkOutDate
			? combineDateAndTime(form.values.checkOutDate, form.values.checkOut)
			: null;
	const checkOutDiff = checkOutDate
		? Math.abs(
				checkOutDate.diff(dayjs.utc(shift.endTimestamp).local(), "minutes"),
			)
		: null;

	const checkInOffline = (shift as { offlineCheckIn?: boolean }).offlineCheckIn;
	const checkOutOffline = (shift as { offlineCheckOut?: boolean })
		.offlineCheckOut;

	const trpc = useTRPC();
	const detailCache = useQuery({
		...trpc.careSheets.getCareSheetCheckOutDetails.queryOptions({
			shift_id: shift.id,
		}),
		enabled: false,
	});

	const requiredFormsQuery = useQuery({
		...trpc.caregiverForms.listMyIncompleteForms.queryOptions(),
	});
	const requiredForm = requiredFormsQuery.data?.find(
		(f) =>
			f.clientId === shift.clientId && f.payerSourceId === shift.payerSourceId,
	);
	const hasIncompleteRequiredForm = !!requiredForm;

	return (
		<>
			<Drawer
				opened={isCallPOCModalOpen}
				onClose={callPOCModalControls.close}
				position="bottom"
				size="100%"
			>
				<Center>
					<Stack align="start" w="360px">
						<Title order={3}>Point of Contacts</Title>
						<Group>
							<Text c="dimmed" fz="sm">
								Client: {shift.clientName}
							</Text>
							{(shift.clientPrimaryPhone || shift.clientSecondaryPhone) && (
								<Anchor
									fz={16}
									href={`tel:${shift.clientPrimaryPhone || shift.clientSecondaryPhone}`}
								>
									{formatPhoneNumber(
										shift.clientPrimaryPhone || shift.clientSecondaryPhone,
									)}
								</Anchor>
							)}
						</Group>
						{shift.clientPointOfContacts?.map((contact) => (
							<Paper
								key={contact.id}
								withBorder
								p="md"
								className="base-100"
								w="100%"
							>
								<Stack gap={4}>
									<Title order={5}>{contact.name}</Title>
									<Text size="sm" c="dimmed">
										{[contact.role, contact.relationship]
											.filter(Boolean)
											.join(" • ")}
									</Text>
								</Stack>
								<Space h="sm" />
								{contact.homePhone && (
									<Group>
										<IconHomeFilled />
										<Anchor fz={16} href={`tel:${contact.homePhone}`}>
											{formatPhoneNumber(contact.homePhone)}
										</Anchor>
									</Group>
								)}
								{contact.workPhone && (
									<Group>
										<IconBuildingSkyscraper />
										<Anchor href={`tel:${contact.workPhone}`}>
											{formatPhoneNumber(contact.workPhone)}
										</Anchor>
									</Group>
								)}
								{contact.mobilePhone && (
									<Group>
										<IconDeviceMobile />
										<Anchor href={`tel:${contact.mobilePhone}`}>
											{formatPhoneNumber(contact.mobilePhone)}
										</Anchor>
									</Group>
								)}
							</Paper>
						))}
					</Stack>
				</Center>
			</Drawer>

			<Drawer
				opened={isActionsDrawerOpen}
				onClose={actionsDrawerControls.close}
				position="bottom"
				size="sm"
				radius={"md"}
				title="Actions"
			>
				<Stack w="100%" gap="sm">
					<Button
						leftSection={<IconMapPinFilled />}
						variant="outline"
						component={Link}
						to={`https://www.google.com/maps/dir/?api=1&destination=${shift.addressLatitude},${shift.addressLongitude}`}
						target="_blank"
						rel="noopener noreferrer"
						fullWidth
					>
						Open in Google Maps
					</Button>

					<Button
						leftSection={<IconPhoneFilled />}
						variant="outline"
						fullWidth
						onClick={() => {
							actionsDrawerControls.close();
							callPOCModalControls.open();
						}}
					>
						Call Point of Contact
					</Button>

					{shift.carePlanId && (
						<Button
							leftSection={<IconReportMedical />}
							variant="outline"
							component={Link}
							to={`/app/care-plans/${shift.carePlanId}`}
							fullWidth
						>
							View Care Plan
						</Button>
					)}

					{shift.careSheetId && (
						<Button
							leftSection={<IconFileDots />}
							variant="outline"
							component={Link}
							to={`/app/care-sheets/${shift.careSheetId}/review`}
							fullWidth
						>
							View Care Sheet
						</Button>
					)}
				</Stack>
			</Drawer>

			{headerDateString ? (
				<Group grow ref={scrollIntoViewTargetRef}>
					<Title order={3}>{headerDateString}</Title>
					<Title
						order={5}
						style={{
							textAlign: "right",
						}}
					>
						{dayBillableHoursMap.get(headerDateString)?.toFixed(2)} hrs
					</Title>
				</Group>
			) : null}

			<Card withBorder radius="md" role="article" aria-label={shift.clientName}>
				<Card.Section className="base-100" withBorder inheritPadding py="sm">
					<Stack gap={4}>
						<Stack gap={0}>
							<Group justify="space-between" wrap="nowrap">
								<Title
									size="h4"
									style={{ textWrap: "wrap", wordBreak: "break-word" }}
								>
									{shift.clientName}
								</Title>

								{detailCache.data ? (
									<Badge
										size="xs"
										color="blue"
										leftSection={<IconCloudDownload size={16} />}
										variant="transparent"
									>
										Available Offline
									</Badge>
								) : null}
							</Group>
							<Text>
								{[
									shift.addressStreet1,
									shift.addressStreet2,
									shift.addressCity,
									shift.addressState,
									shift.addressZip,
								]
									.filter(Boolean)
									.join(" ")}
							</Text>
						</Stack>
						<Group gap="xs">
							<Text>
								{dayjs
									.utc(shift.startTimestamp)
									.local()
									.format("ddd MMM D, YYYY")}
							</Text>
							{checkInOffline && !checkOutOffline ? (
								<Badge size="sm" color="gray">
									Check In Saved Offline
								</Badge>
							) : checkOutOffline ? (
								<Badge size="sm" color="gray">
									Check Out Saved Offline
								</Badge>
							) : (
								getStatusBadge(shift)
							)}
						</Group>
					</Stack>
				</Card.Section>
				<Card.Section withBorder inheritPadding py="md" className="base-50">
					<Stack gap="sm" align="start">
						<Group w="100%" align="start">
							<Stack gap="sm" flex={1}>
								<Stack gap={0}>
									<Text fw={500} fz={14}>
										Scheduled Start
									</Text>
									<Text>
										{dayjs
											.utc(shift.startTimestamp)
											.local()
											.format("MM/DD h:mm A")}
									</Text>
								</Stack>
								{shift.careSheetId ? (
									<Stack gap={0}>
										<Text fz="sm" fw={500}>
											Check In
										</Text>
										<Text>
											{checkInDate
												? `${checkInDate.format("MM/DD h:mm A")}`
												: "--"}
										</Text>
									</Stack>
								) : (
									<>
										<DateInput
											label="Check In Date"
											valueFormat="MM/DD/YYYY"
											value={
												form.values.checkInDate
													? new Date(`${form.values.checkInDate}T00:00:00`)
													: null
											}
											onChange={(date) => {
												form.setFieldValue(
													"checkInDate",
													date ? dayjs(date).format("YYYY-MM-DD") : undefined,
												);
											}}
											disabled={shift.checkInLocked}
										/>
										<TimeInput
											{...form.getInputProps("checkIn")}
											disabled={shift.checkInLocked}
											label="Check In Time"
											ref={checkInInputRef}
											rightSection={
												<ActionIcon
													variant="subtle"
													color="gray"
													onClick={() => checkInInputRef.current?.showPicker()}
													disabled={shift.checkInLocked}
												>
													<IconClock
														style={{ width: "16px", height: "16px" }}
														stroke={1.5}
													/>
												</ActionIcon>
											}
										/>
									</>
								)}
								{checkInDiff &&
								checkInDiff >
									(shift.payerSourceEvvTimeToleranceMinutes || 15) ? (
									<Text c="red" fz="sm">
										Warning: Check in time is significantly different from
										schedule
									</Text>
								) : null}

								{shift.careSheetId ? null : shift.quickCheckInEnabled ? (
									<Button
										onClick={onQuickCheckin}
										loading={checkInOutMutation.isPending || isGpsLoading}
										disabled={hasIncompleteRequiredForm && !shift.checkIn}
									>
										Check In
									</Button>
								) : (
									<Button
										fullWidth
										onClick={onManualCheckIn}
										disabled={
											(hasIncompleteRequiredForm && !shift.checkIn) ||
											!form.isDirty("checkIn") ||
											!form.isValid() ||
											checkInOutMutation.isPending ||
											shift.checkInLocked ||
											isGpsLoading
										}
										loading={
											(checkInOutMutation.isPending || isGpsLoading) &&
											form.isDirty("checkIn")
										}
									>
										Save Check In
									</Button>
								)}
							</Stack>
							<Stack gap="sm" flex={1}>
								<Stack gap={0}>
									<Text fw={500} fz={14}>
										Scheduled End
									</Text>
									<Text>
										{dayjs
											.utc(shift.endTimestamp)
											.local()
											.format("MM/DD h:mm A")}
									</Text>
								</Stack>
								{shift.careSheetId ? (
									<Stack gap={0}>
										<Text fw={500} fz={14}>
											Check Out
										</Text>
										<Text>
											{checkOutDate
												? `${checkOutDate.format("MM/DD h:mm A")}`
												: "--"}
										</Text>
									</Stack>
								) : (
									<>
										<DateInput
											label="Check Out Date"
											valueFormat="MM/DD/YYYY"
											value={
												form.values.checkOutDate
													? new Date(`${form.values.checkOutDate}T00:00:00`)
													: null
											}
											onChange={(date) => {
												form.setFieldValue(
													"checkOutDate",
													date ? dayjs(date).format("YYYY-MM-DD") : undefined,
												);
											}}
											disabled={shift.checkOutLocked}
										/>
										<TimeInput
											{...form.getInputProps("checkOut")}
											disabled={shift.checkOutLocked}
											label="Check Out Time"
											ref={checkOutInputRef}
											rightSection={
												<ActionIcon
													variant="subtle"
													color="gray"
													onClick={() => checkOutInputRef.current?.showPicker()}
													disabled={shift.checkOutLocked}
												>
													<IconClock
														style={{ width: "16px", height: "16px" }}
														stroke={1.5}
													/>
												</ActionIcon>
											}
										/>
									</>
								)}
								{checkOutDiff &&
								checkOutDiff >
									(shift.payerSourceEvvTimeToleranceMinutes || 15) ? (
									<Text c="red" fz="sm">
										Warning: Check out time is significantly different from
										schedule
									</Text>
								) : null}

								{shift.careSheetId ? null : shift.quickCheckOutEnabled ? (
									<Button
										onClick={onQuickCheckOut}
										loading={checkInOutMutation.isPending || isGpsLoading}
										disabled={hasIncompleteRequiredForm}
									>
										Check Out
									</Button>
								) : (
									<Button
										fullWidth
										onClick={onManualCheckOut}
										disabled={
											hasIncompleteRequiredForm ||
											!form.isDirty("checkOut") ||
											!form.isValid() ||
											checkInOutMutation.isPending ||
											shift.checkOutLocked ||
											isGpsLoading
										}
										loading={
											(checkInOutMutation.isPending || isGpsLoading) &&
											form.isDirty("checkOut")
										}
									>
										Save Check Out
									</Button>
								)}
							</Stack>
						</Group>
						{hasIncompleteRequiredForm && (
							<Alert
								color="orange"
								title="Required Form Incomplete"
								icon={<IconAlertTriangle size={16} />}
								w="100%"
							>
								<Stack gap="xs">
									<Text size="sm">
										You must complete the required form before checking in or
										out.
									</Text>
									<Button
										component={Link}
										to={ROUTES.CAREGIVER_APP.FORMS.DETAIL({
											careFormTypeId: requiredForm.careFormTypeId,
											clientId: requiredForm.clientId,
											payerSourceId: requiredForm.payerSourceId,
										})}
										size="xs"
										variant="filled"
										color="orange"
									>
										Complete Form
									</Button>
								</Stack>
							</Alert>
						)}
						{shift.careSheetId ? (
							<Stack w="100%">
								<Button
									component={Link}
									to={`/app/care-sheets/${shift.careSheetId}/shifts/${shift.id}/checkout`}
									disabled={
										hasIncompleteRequiredForm ||
										(!shift.checkIn && shift.checkInLocked)
									}
									onClick={(e: React.MouseEvent) => {
										if (hasIncompleteRequiredForm) {
											e.preventDefault();
										}
									}}
								>
									{!shift.checkIn
										? "Check In"
										: shift.checkIn && !shift.checkOut
											? "Check Out"
											: "Edit"}
								</Button>
							</Stack>
						) : null}

						<Button
							variant="light"
							fullWidth
							onClick={actionsDrawerControls.open}
						>
							Actions
						</Button>
					</Stack>
				</Card.Section>
				{shift.payerSourceEvvIsRequired && (
					<Card.Section withBorder inheritPadding py="md" className="base-100">
						<Text fw={500} fz={14}>
							EVV Details
						</Text>
						<Space h={4} />
						<Group grow>
							<Stack gap="sm">
								<Stack gap={0}>
									<Group gap={4}>
										<IconCalendarFilled size={14} />
										<Text>
											{dayjs(shift.evvCheckIn).isValid()
												? dayjs
														.utc(shift.evvCheckIn)
														.local()
														.format("MM/DD h:mm A")
												: "No Date"}
										</Text>
									</Group>
									{shift.quickCheckInEnabled ? (
										<Group
											align="center"
											c={
												shift.ifCheckedInNow.evvCheckInTimeExceedsTolerance ===
												true
													? "red"
													: shift.ifCheckedInNow
																.evvCheckInTimeExceedsTolerance === false
														? "green"
														: "dimmed"
											}
											gap={4}
											aria-label="EVV check-in time"
										>
											<IconClockFilled size={14} />
											<Text>
												{typeof Number(
													shift.ifCheckedInNow.evvCheckInTimeDiscrepancyMinutes,
												) === "number"
													? Math.round(
															Number(
																shift.ifCheckedInNow
																	.evvCheckInTimeDiscrepancyMinutes,
															),
														)
													: "-- "}
												min
											</Text>
										</Group>
									) : shift.evvCheckInTimeMissing ? (
										<Group
											align="center"
											c="red"
											gap={4}
											aria-label="EVV check-in time"
										>
											<IconClockFilled size={14} />
											<Text>Missing Time</Text>
										</Group>
									) : shift.evvCheckIn ? (
										<Group
											align="center"
											c={
												shift.evvCheckInTimeExceedsTolerance === true
													? "red"
													: shift.evvCheckInTimeExceedsTolerance === false
														? "green"
														: "dimmed"
											}
											gap={4}
											aria-label="EVV check-in time"
										>
											<IconClockFilled size={14} />
											<Text>
												{typeof Number(
													shift.evvCheckInTimeDiscrepancyMinutes,
												) === "number"
													? Math.round(
															Number(shift.evvCheckInTimeDiscrepancyMinutes),
														)
													: "-- "}
												min
											</Text>
										</Group>
									) : (
										<Group
											align="center"
											c="dimmed"
											gap={4}
											aria-label="EVV check-in time"
										>
											<IconClockFilled size={14} />
											<Text>Time</Text>
										</Group>
									)}
									{shift.quickCheckInEnabled ? (
										<Group
											align="center"
											c={
												shift.ifCheckedInNow
													.evvCheckInDistanceExceedsTolerance === false
													? "red"
													: shift.ifCheckedInNow
																.evvCheckInDistanceExceedsTolerance === true
														? "green"
														: "dimmed"
											}
											gap={4}
											aria-label="EVV check-in distance"
										>
											<IconMapPinFilled size={14} />
											<Text>
												{typeof Number(
													shift.ifCheckedInNow.evvCheckInDistanceFt,
												) === "number"
													? Math.round(
															Number(shift.ifCheckedInNow.evvCheckInDistanceFt),
														)
													: "-- "}
												ft
											</Text>
										</Group>
									) : shift.evvCheckInLatLongMissing ? (
										<Group
											align="center"
											c="red"
											gap={4}
											aria-label="EVV check-in distance"
										>
											<IconMapPinFilled size={14} />
											<Text>Missing Geo</Text>
										</Group>
									) : shift.checkInLatitude && shift.checkInLongitude ? (
										<Group
											align="center"
											c={
												shift.evvCheckInDistanceExceedsTolerance === true
													? "red"
													: shift.evvCheckInDistanceExceedsTolerance === false
														? "green"
														: "dimmed"
											}
											gap={4}
											aria-label="EVV check-in distance"
										>
											<IconMapPinFilled size={14} />
											<Text>
												{typeof Number(shift.checkInDistanceFt) === "number"
													? Math.round(Number(shift.checkInDistanceFt))
													: "-- "}
												ft
											</Text>
										</Group>
									) : shift.checkInPhoneNumber ? (
										<Group
											align="center"
											c="green"
											gap={4}
											aria-label="EVV check-in distance"
										>
											<IconPhoneFilled size={14} />
											<Text>{formatPhoneNumber(shift.checkInPhoneNumber)}</Text>
										</Group>
									) : (
										<Group
											align="center"
											c="dimmed"
											gap={4}
											aria-label="EVV check-in distance"
										>
											<IconMapPinFilled size={14} />
											<Text>Geo</Text>
										</Group>
									)}
								</Stack>
							</Stack>
							<Stack gap="sm">
								<Stack gap={0}>
									<Group gap={4}>
										<IconCalendarFilled size={14} />
										<Text>
											{dayjs(shift.evvCheckOut).isValid()
												? dayjs
														.utc(shift.evvCheckOut)
														.local()
														.format("MM/DD h:mm A")
												: "No Date"}
										</Text>
									</Group>
									{shift.quickCheckOutEnabled ? (
										<Group
											align="center"
											c={
												shift.ifCheckedOutNow
													.evvCheckOutTimeExceedsTolerance === false
													? "red"
													: shift.ifCheckedOutNow
																.evvCheckOutTimeExceedsTolerance === true
														? "green"
														: "dimmed"
											}
											gap={4}
											aria-label="EVV check-out time"
										>
											<IconClockFilled size={14} />
											<Text>
												{typeof Number(
													shift.ifCheckedOutNow
														.evvCheckOutTimeDiscrepancyMinutes,
												) === "number"
													? Math.round(
															Number(
																shift.ifCheckedOutNow
																	.evvCheckOutTimeDiscrepancyMinutes,
															),
														)
													: "-- "}
												min
											</Text>
										</Group>
									) : shift.evvCheckOutTimeMissing ? (
										<Group
											align="center"
											c="red"
											gap={4}
											aria-label="EVV check-out time"
										>
											<IconClockFilled size={14} />
											<Text>Missing Time</Text>
										</Group>
									) : shift.evvCheckOut ? (
										<Group
											align="center"
											c={
												shift.evvCheckOutTimeExceedsTolerance === true
													? "red"
													: shift.evvCheckOutTimeExceedsTolerance === false
														? "green"
														: "dimmed"
											}
											gap={4}
											aria-label="EVV check-out time"
										>
											<IconClockFilled size={14} />
											<Text>
												{typeof Number(
													shift.evvCheckOutTimeDiscrepancyMinutes,
												) === "number"
													? Math.round(
															Number(shift.evvCheckOutTimeDiscrepancyMinutes),
														)
													: "-- "}
												min
											</Text>
										</Group>
									) : (
										<Group
											align="center"
											c="dimmed"
											gap={4}
											aria-label="EVV check-out time"
										>
											<IconClockFilled size={14} />
											<Text>Time</Text>
										</Group>
									)}
									{shift.quickCheckOutEnabled ? (
										<Group
											align="center"
											c={
												shift.ifCheckedOutNow
													.evvCheckOutDistanceExceedsTolerance === false
													? "red"
													: shift.ifCheckedOutNow
																.evvCheckOutDistanceExceedsTolerance === true
														? "green"
														: "dimmed"
											}
											gap={4}
											aria-label="EVV check-out distance"
										>
											<IconMapPinFilled size={14} />
											<Text>
												{typeof Number(
													shift.ifCheckedOutNow.evvCheckOutDistanceFt,
												) === "number"
													? Math.round(
															Number(
																shift.ifCheckedOutNow.evvCheckOutDistanceFt,
															),
														)
													: "-- "}
												ft
											</Text>
										</Group>
									) : shift.evvCheckOutLatLongMissing ? (
										<Group
											align="center"
											c="red"
											gap={4}
											aria-label="EVV check-out distance"
										>
											<IconMapPinFilled size={14} />
											<Text>Missing Geo</Text>
										</Group>
									) : shift.checkOutLatitude && shift.checkOutLongitude ? (
										<Group
											align="center"
											c={
												shift.evvCheckOutDistanceExceedsTolerance === true
													? "red"
													: shift.evvCheckOutDistanceExceedsTolerance === false
														? "green"
														: "dimmed"
											}
											gap={4}
											aria-label="EVV check-out distance"
										>
											<IconMapPinFilled size={14} />
											<Text>
												{typeof Number(shift.checkOutDistanceFt) === "number"
													? Math.round(Number(shift.checkOutDistanceFt))
													: "-- "}
												ft
											</Text>
										</Group>
									) : shift.checkOutPhoneNumber ? (
										<Group
											align="center"
											c="green"
											gap={4}
											aria-label="EVV check-out distance"
										>
											<IconPhoneFilled size={14} />
											<Text>
												{formatPhoneNumber(shift.checkOutPhoneNumber)}
											</Text>
										</Group>
									) : (
										<Group
											align="center"
											c="dimmed"
											gap={4}
											aria-label="EVV check-out distance"
										>
											<IconMapPinFilled size={14} />
											<Text>Geo</Text>
										</Group>
									)}
								</Stack>
							</Stack>
						</Group>
					</Card.Section>
				)}
			</Card>
		</>
	);
}
